import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiFilter, FiDownload, FiShare2, FiTrash2, FiEye, FiUpload, FiX } from 'react-icons/fi';
import documentService from '../../services/documentService';
import { documentFolderService } from '../../services/documentService';
import FolderTree from './FolderTree';

const DocumentList = ({ projectId = null }) => {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    file_type: '',
    tags: '',
    is_public: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadFolders();
  }, [projectId]);

  useEffect(() => {
    loadDocuments();
  }, [filters, pagination.page, selectedFolderId, projectId]);

  const loadFolders = async () => {
    try {
      const params = projectId ? { project_id: projectId } : {};
      const data = await documentFolderService.getTree(projectId);
      setFolders(data.data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        folder_id: selectedFolderId,
        project_id: projectId,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });

      const data = await documentService.getAll(params);
      setDocuments(data.data || []);
      if (data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentService.delete(id);
        loadDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document');
      }
    }
  };

  const handleDownload = async (document) => {
    try {
      const data = await documentService.download(document.id);
      // Open download URL in new tab
      window.open(data.data.file_url, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handleCreateFolder = async (folderData) => {
    try {
      await documentFolderService.create({
        ...folderData,
        project_id: projectId
      });
      loadFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder');
    }
  };

  const handleEditFolder = async (folder) => {
    const newName = prompt('Enter new folder name:', folder.name);
    if (newName && newName !== folder.name) {
      try {
        await documentFolderService.update(folder.id, { name: newName });
        loadFolders();
      } catch (error) {
        console.error('Error updating folder:', error);
        alert('Failed to update folder');
      }
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (window.confirm('Are you sure you want to delete this folder? This will also delete all documents inside.')) {
      try {
        await documentFolderService.delete(folderId, true);
        loadFolders();
        if (selectedFolderId === folderId) {
          setSelectedFolderId(null);
        }
      } catch (error) {
        console.error('Error deleting folder:', error);
        alert('Failed to delete folder');
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      file_type: '',
      tags: '',
      is_public: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading documents...</div>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      {/* Folder Tree Sidebar */}
      <div className="w-64 flex-shrink-0">
        <FolderTree
          folders={folders}
          selectedFolderId={selectedFolderId}
          onFolderSelect={setSelectedFolderId}
          onCreateFolder={handleCreateFolder}
          onEditFolder={handleEditFolder}
          onDeleteFolder={handleDeleteFolder}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
            <p className="text-sm text-gray-600 mt-1">
              {pagination.total} document{pagination.total !== 1 ? 's' : ''} found
            </p>
          </div>
          <Link
            to={projectId ? `/app/projects/${projectId}/documents/upload` : '/app/documents/upload'}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiUpload className="mr-2" size={18} />
            Upload Document
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search documents..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiFilter className="mr-2" size={18} />
              Filters
            </button>

            {/* Clear Filters */}
            {(filters.search || filters.file_type || filters.tags || filters.is_public) && (
              <button
                onClick={clearFilters}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                <FiX className="mr-1" size={18} />
                Clear
              </button>
            )}
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File Type
                </label>
                <select
                  name="file_type"
                  value={filters.file_type}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Types</option>
                  <option value="pdf">PDF</option>
                  <option value="doc">Word</option>
                  <option value="xls">Excel</option>
                  <option value="ppt">PowerPoint</option>
                  <option value="jpg">Images</option>
                  <option value="zip">Archives</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  name="tags"
                  value={filters.tags}
                  onChange={handleFilterChange}
                  placeholder="Enter tags..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visibility
                </label>
                <select
                  name="is_public"
                  value={filters.is_public}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All</option>
                  <option value="true">Public</option>
                  <option value="false">Private</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Documents Grid */}
        {documents.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="text-gray-400 mb-4">
              <FiUpload size={64} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
            <p className="text-gray-600 mb-4">
              {selectedFolderId ? 'This folder is empty' : 'Upload your first document to get started'}
            </p>
            <Link
              to={projectId ? `/app/projects/${projectId}/documents/upload` : '/app/documents/upload'}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiUpload className="mr-2" size={18} />
              Upload Document
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map(document => (
              <div
                key={document.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                {/* File Icon and Type */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <span className="text-3xl mr-3">
                      {documentService.getFileTypeIcon(document.file_type)}
                    </span>
                    <div>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                        documentService.getFileTypeColor(document.file_type)
                      }`}>
                        {document.file_type?.toUpperCase() || 'FILE'}
                      </span>
                    </div>
                  </div>
                  {document.is_public && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                      Public
                    </span>
                  )}
                </div>

                {/* Document Title */}
                <Link
                  to={`/app/documents/${document.id}`}
                  className="block font-medium text-gray-900 hover:text-blue-600 mb-2 truncate"
                  title={document.title}
                >
                  {document.title}
                </Link>

                {/* File Info */}
                <div className="text-sm text-gray-600 mb-3">
                  <div className="flex items-center justify-between">
                    <span>{documentService.formatFileSize(document.file_size)}</span>
                    <span>v{document.version}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {new Date(document.created_at).toLocaleDateString()}
                  </div>
                </div>

                {/* Tags */}
                {document.tags && Array.isArray(document.tags) && document.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {document.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {document.tags.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{document.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}
                {document.tags && typeof document.tags === 'string' && document.tags !== '[]' && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {JSON.parse(document.tags).slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {JSON.parse(document.tags).length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{JSON.parse(document.tags).length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/app/documents/${document.id}`}
                      className="p-2 text-gray-600 hover:text-blue-600 rounded transition-colors"
                      title="View details"
                    >
                      <FiEye size={16} />
                    </Link>
                    <button
                      onClick={() => handleDownload(document)}
                      className="p-2 text-gray-600 hover:text-green-600 rounded transition-colors"
                      title="Download"
                    >
                      <FiDownload size={16} />
                    </button>
                    <button
                      className="p-2 text-gray-600 hover:text-purple-600 rounded transition-colors"
                      title="Share"
                    >
                      <FiShare2 size={16} />
                    </button>
                  </div>
                  <button
                    onClick={() => handleDelete(document.id)}
                    className="p-2 text-gray-600 hover:text-red-600 rounded transition-colors"
                    title="Delete"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentList;


